/**
 * What a share from the canvas says about itself before the transcript starts.
 *
 * A canvas share and a chat share are both conversations, and read the same way
 * once they start. What differs is everything around the words: this came from a
 * plane where the thing had a name, sat among other things, and was **fed by
 * them**. A transcript cannot hold any of that, so it goes here — above the
 * conversation, once, rather than woven through it.
 *
 * The part that matters is what fed it. An answer built on three earlier
 * answers, published alone, is a conclusion with its reasoning removed; a
 * reader has no way to tell that from an answer that stands on its own. So both
 * are stated, and the second is stated explicitly rather than by silence.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Workflow } from 'lucide-react'
import type { AirSharePresentation, SharedFile } from '@synti/core'

export function AirShareHeader({
  air,
  files,
}: {
  air: AirSharePresentation
  /** The share's own filesystem, where a cover picture is found if it travelled. */
  files: Record<string, SharedFile>
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const inputs = air.inputs ?? []
  const cover = air.cover ? files[air.cover] : undefined

  return (
    <div className="border-b border-border bg-background">
      {/* A cover only if it travelled. A share whose picture stayed behind looks
          exactly like one that never had a picture, which is the truth. */}
      {cover && (
        <div className="h-28 w-full overflow-hidden sm:h-36">
          <img
            src={`data:${cover.mime};base64,${cover.data}`}
            alt=""
            className="h-full w-full object-cover"
          />
        </div>
      )}
      <div className="mx-auto flex max-w-3xl flex-col gap-2 px-4 py-3 sm:px-6">
        <div className="flex items-center gap-2.5">
          {air.icon ? (
            <span className="text-lg leading-none">{air.icon}</span>
          ) : (
            <Workflow className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <div className="min-w-0">
            {air.title && (
              <div className="truncate font-medium text-foreground">{air.title}</div>
            )}
            <div className="text-xs text-muted-foreground">
              {t(air.kind === 'node' ? 'air.share.fromCanvasNode' : 'air.share.fromCanvasChat')}
            </div>
          </div>
        </div>

        {inputs.length === 0 ? (
          // Said out loud. Without it, "no context shown" and "context withheld"
          // look identical to a reader, and only one of them is honest.
          <div className="text-xs text-muted-foreground">{t('air.share.fedByNothing')}</div>
        ) : (
          <div className="text-xs">
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground"
              aria-expanded={open}
            >
              <ChevronDown
                className={`h-3 w-3 transition-transform ${open ? '' : '-rotate-90'}`}
              />
              {t('air.share.fedBy', { count: inputs.length })}
            </button>
            {open && (
              <ul className="mt-2 space-y-1.5 border-l border-border pl-3">
                {inputs.map((prompt, i) => (
                  <li key={i} className="text-muted-foreground">
                    {prompt}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
